import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, MapPin, User, Calendar, Clock, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import { Loader, ImageUploader } from "@/components/common";
import { INSTALL_STATUS, INSTALL_STEPS, statusMeta } from "@/lib/labels";
import { TextArea, TextField } from "@/components/Modal";
import { toast } from "sonner";

const NEXT = {
  acceptee: { to: "en_route", label: "Je suis en route" },
  en_route: { to: "arrive", label: "Arrivé chez le client" },
  arrive: { to: "commencee", label: "Commencer l'installation" },
  commencee: { to: "terminee", label: "Terminer et transmettre pour validation" },
};

export default function ProMissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [refusing, setRefusing] = useState(false);
  const [reason, setReason] = useState("");
  const [report, setReport] = useState({ difficulties: "", extra_material: "", client_signature: "" });

  const load = useCallback(() => {
    api.get(`/installations/${id}`).then((r) => { setM(r.data); setReport({ difficulties: r.data.difficulties || "", extra_material: r.data.extra_material || "", client_signature: r.data.client_signature || "" }); });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!m) return <Loader />;
  const meta = statusMeta(INSTALL_STATUS, m.status);
  const step = INSTALL_STEPS.indexOf(m.status);
  const next = NEXT[m.status];

  const changeStatus = async (to) => {
    try { await api.put(`/installations/${id}/status`, null, { params: { status: to } }); toast.success("Statut mis à jour"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const respond = async (action) => {
    try {
      await api.put(`/installations/${id}/respond`, { action, reason });
      toast.success(action === "accept" ? "Mission acceptée" : "Mission refusée");
      setRefusing(false); setReason(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const savePhotos = async (field, urls) => {
    try { await api.put(`/installations/${id}/report`, { [field]: urls }); setM((s) => ({ ...s, [field]: urls })); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveReport = async () => {
    try { await api.put(`/installations/${id}/report`, report); toast.success("Rapport enregistré"); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <button onClick={() => navigate("/pro")} className="flex items-center gap-1 text-sm text-slate-400 mb-4"><ArrowLeft className="w-4 h-4" /> Mes missions</button>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <h1 className="font-display text-2xl font-black">{m.mission_number}</h1>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.color}`}>{meta.label}</span>
        {m.urgency === "urgent" && <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">⚡ URGENT</span>}
      </div>

      <Card>
        <Row icon={User} label="Client" value={m.customer_name} />
        <Row icon={Phone} label="Téléphone" value={<a href={`tel:${m.phone}`} className="text-[#E08B00] underline">{m.phone || "—"}</a>} />
        <Row icon={MapPin} label="Adresse" value={`${m.address} (${m.zone})`} />
        <Row icon={Calendar} label="Date" value={m.scheduled_date ? formatDate(m.scheduled_date) : "À convenir"} />
        <Row icon={Clock} label="Heure" value={m.scheduled_time || "—"} />
      </Card>

      <Card title="Prestation">
        <p className="text-sm text-slate-200 font-semibold">{m.product_name}</p>
        {m.description && <p className="text-sm text-slate-400 mt-1">{m.description}</p>}
        {m.pro_amount > 0 && <p className="font-num text-[#E08B00] font-bold text-lg mt-2">{formatFCFA(m.pro_amount)}</p>}
      </Card>

      {/* Progress */}
      <Card title="Avancement">
        {m.status === "refusee" ? (
          <p className="text-sm text-red-400 font-semibold text-center">Mission refusée{m.refusal_reason ? ` — ${m.refusal_reason}` : ""}. En attente de réaffectation par le back-office.</p>
        ) : (
          <>
            <div className="flex gap-1 mb-3">
              {INSTALL_STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#E08B00]" : "bg-slate-700"}`} />)}
            </div>
            {m.status === "affectee" ? (
              refusing ? (
                <div className="space-y-2">
                  <DarkArea label="Motif du refus" value={reason} onChange={setReason} testid="refuse-reason" />
                  <div className="flex gap-2">
                    <button onClick={() => setRefusing(false)} className="flex-1 border border-slate-600 text-slate-300 text-sm font-semibold py-2 rounded-lg" data-testid="refuse-cancel">Annuler</button>
                    <button onClick={() => respond("refuse")} className="flex-1 bg-red-600 text-white text-sm font-bold py-2 rounded-lg" data-testid="refuse-confirm">Confirmer le refus</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => respond("accept")} className="flex-1 bg-[#E08B00] text-[#0F172A] font-black py-3 rounded-lg active:scale-95 transition" data-testid="mission-accept">Accepter la mission</button>
                  <button onClick={() => setRefusing(true)} className="flex-1 border border-red-500 text-red-400 font-bold py-3 rounded-lg flex items-center justify-center gap-1" data-testid="mission-refuse"><AlertTriangle className="w-4 h-4" /> Refuser</button>
                </div>
              )
            ) : next ? (
              <button onClick={() => changeStatus(next.to)} className="w-full bg-[#E08B00] text-[#0F172A] font-black py-3 rounded-lg active:scale-95 transition" data-testid="mission-next-status">
                {next.label}
              </button>
            ) : m.status === "terminee" ? (
              <p className="text-sm text-teal-400 font-semibold text-center">✓ Transmise — en attente de validation par le back-office</p>
            ) : (
              <p className="text-sm text-green-400 font-semibold text-center">✓ Intervention {m.status === "validee" ? "validée" : "clôturée"}</p>
            )}
          </>
        )}
      </Card>

      {/* Photos */}
      <Card title="Photos de l'intervention">
        <PhotoBlock label="Avant intervention" value={m.before_photos || []} onChange={(u) => savePhotos("before_photos", u)} testid="photos-before" />
        <PhotoBlock label="Pendant intervention" value={m.during_photos || []} onChange={(u) => savePhotos("during_photos", u)} testid="photos-during" />
        <PhotoBlock label="Après intervention" value={m.after_photos || []} onChange={(u) => savePhotos("after_photos", u)} testid="photos-after" />
      </Card>

      {/* Report */}
      <Card title="Rapport & confirmation">
        <div className="space-y-3">
          <DarkArea label="Difficultés rencontrées" value={report.difficulties} onChange={(v) => setReport((s) => ({ ...s, difficulties: v }))} testid="report-difficulties" />
          <DarkArea label="Matériel supplémentaire nécessaire" value={report.extra_material} onChange={(v) => setReport((s) => ({ ...s, extra_material: v }))} testid="report-extra" />
          <DarkField label="Confirmation / signature du client (nom)" value={report.client_signature} onChange={(v) => setReport((s) => ({ ...s, client_signature: v }))} testid="report-signature" />
          <button onClick={saveReport} className="w-full bg-slate-700 text-white font-bold py-2.5 rounded-lg" data-testid="report-save">Enregistrer le rapport</button>
        </div>
      </Card>
    </div>
  );
}

function Card({ title, children }) {
  return <div className="bg-[#1F2937] border border-slate-700 rounded-xl p-4 mb-4">{title && <h2 className="font-display text-lg font-bold mb-3">{title}</h2>}{children}</div>;
}
function Row({ icon: Icon, label, value }) {
  return <div className="flex items-center gap-2 py-1.5 text-sm"><Icon className="w-4 h-4 text-slate-500" /><span className="text-slate-400 w-24">{label}</span><span className="text-slate-100 font-medium">{value}</span></div>;
}
function PhotoBlock({ label, value, onChange, testid }) {
  return (
    <div className="mb-3">
      <p className="text-sm font-semibold text-slate-300 mb-1">{label}</p>
      <ImageUploader value={value} onChange={onChange} testid={testid} label="+ Photo" />
    </div>
  );
}
function DarkArea({ label, value, onChange, testid }) {
  return <label className="block"><span className="text-sm font-semibold text-slate-300">{label}</span>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-1 w-full bg-[#111827] border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-[#E08B00]/40" data-testid={testid} /></label>;
}
function DarkField({ label, value, onChange, testid }) {
  return <label className="block"><span className="text-sm font-semibold text-slate-300">{label}</span>
    <input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-[#111827] border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-[#E08B00]/40" data-testid={testid} /></label>;
}
